import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { SharedConstants, SharedCssClassNames, SharedIconTypes } from '@app/eds';

export type autocompleteOptionsType = {
  id: number;
  name: string;
  value?: number;
  showIcon?: boolean;
  iconName?: string;
  iconSize?: string;
  iconClass?: string;
};

@Component({
  selector: 'app-auto-complete',
  templateUrl: './auto-complete.component.html',
  styleUrls: ['./auto-complete.component.scss']
})
export class AutoCompleteComponent implements OnInit {
  // Placeholder for the input field
  @Input() placeholder!: string;

  // CustomError stores boolean for value if error occured
  @Input() customError = false;

  // custom icon name
  @Input() customIcon: string = SharedIconTypes.ERROR;

  // CustomErrorMessage  stored the error message
  @Input() customErrorMessage!: string;

  // Options to be displayed in the dropdown
  @Input() options!: autocompleteOptionsType[];

  // Event emitter to emit an event when any option is chosen or any input is entered
  @Output() selectedValueEmitter = new EventEmitter<autocompleteOptionsType | string>();

  // This function will emit the event.target.value on pressing enter key.
  @Output() enterPress: EventEmitter<string> = new EventEmitter<string>();

  // Form control for the auto-complete input form
  autocompleteForm = new FormControl();

  // Options filtered based on the user's input
  filteredOptions!: Observable<autocompleteOptionsType[]>;

  ngOnInit() {
    this.filterOptions();
  }

  /**
   * This function is called when there is a change in the user's input.
   * Calls filter function to filter the options out based on the user's input.
   */
  filterOptions = () => {
    this.filteredOptions = this.autocompleteForm?.valueChanges?.pipe(
      startWith(''),
      map((value) => this.filter(value))
    );
  };

  /**
   * This function filters the options out based on the user's input.
   *
   * @param {string}  value: The options are filtered out based on this value.
   * @returns the array of filtered options.
   */
  filter = (value: string): autocompleteOptionsType[] => {
    const filterValue = value?.toLowerCase();
    return this.options?.filter((option) =>
      option?.name?.toLowerCase()?.includes(filterValue)
    );
  };

  /**
   * Used by mat auto complete to autocomplete an object.
   *
   * @param {autocompleteOptionsType} option
   * @returns {string} option name
   */
  displayMatAutoData = (option: autocompleteOptionsType): string =>
    option?.name ?? SharedConstants.EMPTY_STRING;

  /**
   * This function is called when the user manually enters an input.
   *
   * @param event
   */
  onInputChange = (event: any) => {
    const changedValue = event.target.value;
    this.selectedValueEmitter.emit(changedValue);
  };

  /**
   * This function will trigger on pressing the enter key.
   * It will bind the event.target.value to the input
   *
   * @param event
   */
  onKeyEnter = (event: any) => this.enterPress.emit(event.target.value);

  /**
   * This function is called when the user selects one of the options from the dropdown.
   *
   * @param event
   */
  onSelectionChange = (event: any) => {
    const changedValue = event.option.value;
    this.selectedValueEmitter.emit(changedValue);
  };

  /**
   * This method return the classname when there is a custom error
   *
   * return {string} classname
   */
  getClassName = (): string =>
    this.customError
      ? `${SharedCssClassNames.ERROR_INPUT} ${this.getClassNameForIcon(this.customIcon)}`
      : SharedConstants.EMPTY_STRING;

  /**
   * Gets classname for icon
   *
   * @param {string} icon
   * @returns {string}
   */
  getClassNameForIcon = (icon: string) => {
    const classNames: typeof SharedCssClassNames = SharedCssClassNames,
      iconTypes: typeof SharedIconTypes = SharedIconTypes;
    switch (icon) {
      case iconTypes.ERROR:
        return classNames.RED_TEXT;
      case iconTypes.WARNING:
        return classNames.YELLOW_TEXT;
      default:
        return SharedConstants.EMPTY_STRING;
    }
  };
}
